/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';
import {ModelResponse, nullify, ResponseData} from "@dvrd/dvr-controls";
import IDate from '@dvrd/idate';
import {Fulfilment, ReturnHandlingResult, ReturnProcessingResult} from "../util/interfaces";
import Account from "./accountModel";
import Invoice from "./invoiceModel";
import {creditReturnItem, getBolReturns} from "../util/requestSender";
import BolOpenProduct from "./bolOpenProductModel";

interface Props extends BaseProps {
    return_id?: string;
    rma_id?: string;
    order_id?: string;
    ean?: string;
    quantity?: number;
    return_reason?: string;
    return_detail?: string;
    comment?: string;
    fulfilment?: Fulfilment;
    registered_at?: string;
    processed_at?: string;
    processing_result?: ReturnProcessingResult;
    handling_result?: ReturnHandlingResult | null;
    is_credited?: boolean;
    credit_sent?: boolean;
    account_id?: string;
    invoice_id?: string;
    credit_invoice_id?: string;
    account?: Account | any | null;
    invoice?: Invoice | any | null;
    credit_invoice?: Invoice | any | null;
    product?: BolOpenProduct | any | null;
}

interface Fields extends BaseFields {
    return_id: string;
    rma_id: string;
    order_id: string;
    ean: string;
    quantity: number;
    return_reason: string;
    return_detail: string;
    comment: string;
    fulfilment: Fulfilment;
    registered_at: string;
    processed_at: string;
    processing_result: ReturnProcessingResult;
    handling_result: ReturnHandlingResult | null;
    is_credited: boolean;
    credit_sent: boolean;
    image: string | null;
    account_id: string;
    invoice_id: string;
    credit_invoice_id: string;
    account: Account | null;
    invoice: Invoice | null;
    credit_invoice: Invoice | null;
    product: BolOpenProduct | null;
}

const fields: Fields = {
    id: '',
    created_at: '',
    updated_at: '',
    return_id: '',
    rma_id: '',
    order_id: '',
    ean: '',
    quantity: 0,
    return_reason: '',
    return_detail: '',
    comment: '',
    fulfilment: Fulfilment.FBR,
    registered_at: '',
    processed_at: '',
    processing_result: ReturnProcessingResult.OPEN,
    handling_result: null,
    is_credited: false,
    credit_sent: false,
    image: null,
    account_id: '',
    invoice_id: '',
    credit_invoice_id: '',
    account: null,
    invoice: null,
    credit_invoice: null,
    product: null,
};

const fieldsDefinition: { [key in keyof Omit<Fields, 'account' | 'invoice' | 'credit_invoice' | 'product'>]: FieldDefinition } = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    return_id: createFieldDef(ModelFieldType.STRING),
    rma_id: createFieldDef(ModelFieldType.STRING),
    order_id: createFieldDef(ModelFieldType.STRING),
    ean: createFieldDef(ModelFieldType.STRING),
    quantity: createFieldDef(ModelFieldType.NUMBER),
    return_reason: createFieldDef(ModelFieldType.STRING),
    return_detail: createFieldDef(ModelFieldType.STRING),
    comment: createFieldDef(ModelFieldType.STRING),
    registered_at: createFieldDef(ModelFieldType.STRING),
    processed_at: createFieldDef(ModelFieldType.STRING),
    account_id: createFieldDef(ModelFieldType.STRING),
    fulfilment: createFieldDef(ModelFieldType.STRING, Fulfilment.FBR),
    handling_result: createFieldDef(ModelFieldType.STRING, null, true),
    processing_result: createFieldDef(ModelFieldType.STRING, ReturnProcessingResult.OPEN),
    invoice_id: createFieldDef(ModelFieldType.STRING),
    is_credited: createFieldDef(ModelFieldType.BOOLEAN),
    credit_sent: createFieldDef(ModelFieldType.BOOLEAN),
    credit_invoice_id: createFieldDef(ModelFieldType.STRING),
    image: createFieldDef(ModelFieldType.STRING, null, true),
};

const ignoreProps: Array<keyof Props> = ['account', 'invoice', 'credit_invoice', 'product'];

export default class BolReturn extends BaseModel<Props, Fields> {
    static getAll = (accountId: string, search: string, status: ReturnProcessingResult | null, limit: number,
                     page: number, callback: ModelResponse<BolReturn[]>) => {
        return getBolReturns({
            data: {
                account_id: accountId,
                page,
                limit,
                status,
                search: nullify(search),
            }, callback: (data: ResponseData) => {
                const returns: BolReturn[] = data.success ? data.returns.map((ret: any) => new BolReturn(ret)) : [];
                callback(returns, data.success, data);
            }
        });
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get returnId(): string {
        return this.fields.return_id;
    }

    set returnId(return_id: string) {
        this.fields.return_id = return_id;
    }

    get rmaId(): string {
        return this.fields.rma_id;
    }

    set rmaId(rma_id: string) {
        this.fields.rma_id = rma_id;
    }

    get orderId(): string {
        return this.fields.order_id;
    }

    set orderId(order_id: string) {
        this.fields.order_id = order_id;
    }

    get ean(): string {
        return this.fields.ean;
    }

    set ean(ean: string) {
        this.fields.ean = ean;
    }

    get quantity(): number {
        return this.fields.quantity;
    }

    set quantity(quantity: number) {
        this.fields.quantity = quantity;
    }

    get returnReason(): string {
        return this.fields.return_reason;
    }

    set returnReason(return_reason: string) {
        this.fields.return_reason = return_reason;
    }

    get returnDetail(): string {
        return this.fields.return_detail;
    }

    set returnDetail(return_detail: string) {
        this.fields.return_detail = return_detail;
    }

    get comment(): string {
        return this.fields.comment;
    }

    set comment(comment: string) {
        this.fields.comment = comment;
    }

    get fulfilment(): Fulfilment {
        return this.fields.fulfilment;
    }

    set fulfilment(fulfilment: Fulfilment) {
        this.fields.fulfilment = fulfilment;
    }

    get registeredAt(): string {
        return this.fields.registered_at;
    }

    set registeredAt(registered_at: string) {
        this.fields.registered_at = registered_at;
    }

    get registeredAtMoment(): IDate {
        return new IDate(this.registeredAt);
    }

    get processedAt(): string {
        return this.fields.processed_at;
    }

    set processedAt(processed_at: string) {
        this.fields.processed_at = processed_at;
    }

    get processedAtMoment(): IDate | null {
        return this.processedAt.length ? new IDate(this.processedAt) : null;
    }

    get processingResult(): ReturnProcessingResult {
        return this.fields.processing_result;
    }

    set processingResult(processing_result: ReturnProcessingResult) {
        this.fields.processing_result = processing_result;
    }

    get handlingResult(): ReturnHandlingResult | null {
        return this.fields.handling_result;
    }

    set handlingResult(handling_result: ReturnHandlingResult | null) {
        this.fields.handling_result = handling_result;
    }

    get accountId(): string {
        return this.fields.account_id;
    }

    set accountId(account_id: string) {
        this.fields.account_id = account_id;
    }

    get account(): Account | null {
        return this.fields.account;
    }

    set account(account: Account | null) {
        this.fields.account = account;
        if (account) this.fields.account_id = account.id;
    }

    get invoiceId(): string {
        return this.fields.invoice_id;
    }

    set invoiceId(id: string) {
        this.fields.invoice_id = id;
    }

    get invoice(): Invoice | null {
        return this.fields.invoice;
    }

    set invoice(invoice: Invoice | null) {
        this.fields.invoice = invoice;
        if (invoice) this.fields.invoice_id = invoice.id;
    }

    get isCredited(): boolean {
        return this.fields.is_credited;
    }

    set isCredited(isCredited: boolean) {
        this.fields.is_credited = isCredited;
    }

    get creditSent(): boolean {
        if (this.fields.credit_invoice) return this.fields.credit_invoice.isSent;
        return this.fields.credit_sent;
    }

    set creditSent(sent: boolean) {
        this.fields.credit_sent = sent;
    }

    get creditInvoiceId(): string {
        return this.fields.credit_invoice_id;
    }

    set creditInvoiceId(id: string) {
        this.fields.credit_invoice_id = id;
    }

    get creditInvoice(): Invoice | null {
        return this.fields.credit_invoice;
    }

    set creditInvoice(invoice: Invoice | null) {
        this.fields.credit_invoice = invoice;
        if (invoice) this.fields.credit_invoice_id = invoice.id;
    }

    get image(): string | null {
        return this.fields.image;
    }

    set image(image: string | null) {
        this.fields.image = image;
    }

    get translatedStatus(): string {
        const {processingResult} = this;
        switch (processingResult) {
            case ReturnProcessingResult.OPEN:
                if (this.handlingResult === ReturnHandlingResult.EXPIRED)
                    return 'Verlopen';
                else if (this.handlingResult === ReturnHandlingResult.CANCELLED_BY_CUSTOMER)
                    return 'Geannuleerd';
                return 'Openstaand';
            case ReturnProcessingResult.ACCEPTED:
                return 'Verwerkt';
            case ReturnProcessingResult.CANCELED:
                return 'Geannuleerd';
            case ReturnProcessingResult.REJECTED:
                return 'Geweigerd';
        }
    }

    get translatedHandling(): string {
        const {handlingResult} = this;
        switch (handlingResult) {
            case null:
                return 'n.v.t.';
            case ReturnHandlingResult.EXCESSIVE_RETURN:
                return 'Teveel geretourneerd';
            case ReturnHandlingResult.CANCELLED_BY_CUSTOMER:
                return 'Geannuleerd door klant';
            case ReturnHandlingResult.CUSTOMER_KEEPS_PRODUCT_FREE_OF_CHARGE:
                return 'Klant houdt product (gratis)';
            case ReturnHandlingResult.RETURN_RECEIVED:
            case ReturnHandlingResult.STILL_APPROVED:
                return 'Retour ontvangen';
            case ReturnHandlingResult.RETURN_ITEM_LOST:
                return 'Retourzending verloren';
            case ReturnHandlingResult.CUSTOMER_KEEPS_PRODUCT_PAID:
                return 'Klant houdt product (betaald)';
            case ReturnHandlingResult.EXCHANGE_PRODUCT:
                return 'Product omgeruild';
            case ReturnHandlingResult.EXPIRED:
                return 'Verlopen';
            case ReturnHandlingResult.FAILED_TO_CREATE_SHIPPING_LABEL:
                return 'Fout bij transport';
            case ReturnHandlingResult.REPAIR_PRODUCT:
                return 'Reparatie product';
            case  ReturnHandlingResult.RETURN_DOES_NOT_MEET_CONDITIONS:
                return 'Retour voldoet niet aan de voorwaarden';
            case ReturnHandlingResult.STILL_RECEIVED:
                return 'Retour alsnog ontvangen (na verlies)';
            case ReturnHandlingResult.UNDER_INVESTIGATION:
                return 'Wordt onderzocht door Bol';
        }
    }

    get statusColor(): string {
        const {processingResult} = this;
        switch (processingResult) {
            case ReturnProcessingResult.OPEN:
                if (this.handlingResult === ReturnHandlingResult.EXPIRED)
                    return 'red';
                else if (this.handlingResult === ReturnHandlingResult.CANCELLED_BY_CUSTOMER)
                    return 'gray';
                return 'orange';
            case ReturnProcessingResult.ACCEPTED:
                return 'green';
            case ReturnProcessingResult.CANCELED:
                return 'gray';
            case ReturnProcessingResult.REJECTED:
                return 'red';
        }
    }

    get product(): BolOpenProduct | null {
        return this.fields.product;
    }

    createCredit = (creditAll: boolean | undefined, callback: (success: boolean | undefined, data: ResponseData) => void) => {
        creditReturnItem({
            data: {
                account_id: this.accountId,
                return_id: this.returnId,
                rma_id: this.rmaId,
                credit_all_items: creditAll
            }, callback: (data: ResponseData) => {
                callback(data.success, data);
            }
        });
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            if (props.account) {
                this.fields.account = props.account instanceof Account ? props.account : new Account(props.account);
            }
            if (props.invoice) {
                this.fields.invoice = props.invoice instanceof Invoice ? props.invoice : new Invoice(props.invoice);
            }
            if (props.credit_invoice) {
                this.fields.credit_invoice = props.credit_invoice instanceof Invoice ? props.credit_invoice : new Invoice(props.credit_invoice);
            }
            if (props.product) {
                this.fields.product = props.product instanceof BolOpenProduct ? props.product : new BolOpenProduct(props.product);
            }
        }
    };
}